// src/pages/Database.jsx
import React, { useState, useEffect, useMemo } from "react";
import Footer from "../components/Footer";
import { useNavigate } from "react-router-dom";
import { apiUrl } from "../lib/api";
import {
  Database as DatabaseIcon,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Columns3,
  Check,
} from "lucide-react";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";

// Format genome size nicely
const formatGenomeSize = (bp) => {
  if (!bp) return "-";
  if (bp >= 1_000_000) return (bp / 1_000_000).toFixed(2) + " Mbp";
  return (bp / 1_000).toFixed(1) + " kbp";
};

const Database = () => {
  const navigate = useNavigate();

  const [genomes, setGenomes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const computedPageCount = total ? Math.ceil(total / pageSize) : 0;
  const pageStart = total ? pageIndex * pageSize + 1 : 0;
  const pageEnd = total ? Math.min((pageIndex + 1) * pageSize, total) : 0;
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageEnd < total;

  // Debounce search input before hitting the API
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedFilter(globalFilter), 300);
    return () => clearTimeout(handle);
  }, [globalFilter]);

  // Reset to first page when search changes
  useEffect(() => {
    setPageIndex(0);
  }, [debouncedFilter]);

  // Server-side fetch for the current page/sort/search
  useEffect(() => {
    const fetchPage = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("limit", pageSize);
        params.set("offset", pageIndex * pageSize);
        if (debouncedFilter) params.set("search", debouncedFilter);

        // Server-side sorting (only whitelisted columns are honored by backend)
        if (sorting?.length) {
          const sortItem = sorting[0];
          const sortMap = {
            contig_size: "size",
            trimmed_portion_bp: "trimmed_portion",
          };
          params.set("sort", sortMap[sortItem.id] || "refseq");
          params.set("order", sortItem.desc ? "desc" : "asc");
        }

        const res = await fetch(apiUrl(`/genomes?${params.toString()}`));
        const data = await res.json();
        setGenomes(data.results || []);
        setTotal(data.total ?? data.count ?? (data.results || []).length);
      } catch (error) {
        console.error("Failed to fetch genomes:", error);
        setGenomes([]);
        setTotal(0);
      }
      setLoading(false);
    };

    fetchPage();
  }, [pageIndex, pageSize, debouncedFilter, sorting]);

  // Keep page index in bounds if total shrinks
  useEffect(() => {
    if (computedPageCount > 0 && pageIndex >= computedPageCount) {
      setPageIndex(Math.max(0, computedPageCount - 1));
    }
  }, [computedPageCount, pageIndex]);

  const setPagination = (updater) => {
    setPageIndex((prevIndex) => {
      const current = { pageIndex: prevIndex, pageSize };
      const next = typeof updater === "function" ? updater(current) : updater;
      return next.pageIndex ?? prevIndex;
    });
    setPageSize((prevSize) => {
      const current = { pageIndex, pageSize: prevSize };
      const next = typeof updater === "function" ? updater(current) : updater;
      return next.pageSize ?? prevSize;
    });
  };

  // Column definitions
  const columns = useMemo(
    () => [
      {
        accessorKey: "refseq",
        header: "RefSeq ID",
        cell: (info) => (
          <span className="font-semibold">{info.getValue()}</span>
        ),
        size: 180,
      },
      {
        accessorKey: "organism",
        header: "Organism",
        size: 300,
      },
      {
        accessorKey: "ncid",
        header: "NC ID",
        size: 150,
      },
      {
        accessorKey: "contig_size",
        header: "Size",
        cell: (info) => formatGenomeSize(info.getValue()),
        size: 140,
        sortingFn: "basic",
      },
      {
        accessorKey: "contigs",
        header: "Contigs",
        cell: (info) => info.getValue() ?? "–",
        size: 100,
      },
      {
        accessorKey: "oriC_coords",
        header: "OriC",
        cell: (info) => info.getValue() || "–",
        size: 200,
      },
      {
        accessorKey: "dnaA_coords",
        header: "DnaA",
        cell: (info) => info.getValue() || "–",
        size: 200,
      },
      {
        accessorKey: "trimmed_portion_bp",
        header: "Trimmed portion",
        cell: (info) => {
          const oriC = info.row.original.oriC_coords;
          const value = info.getValue();

          if (!oriC || oriC === "-" || oriC === "Not found") {
            return "–";
          }

          return value ?? "–";
        },
        size: 200,
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        size: 150,
        cell: ({ row }) => (
          <button
            className="text-violet-700 font-medium hover:underline"
            onClick={async () => {
              const response = await fetch(
                apiUrl(`/genomes/${encodeURIComponent(row.original.refseq)}`)
              );
              const details = await response.json();
              navigate("/result", { state: { result: details } });
            }}
          >
            View Details →
          </button>
        ),
      },
    ],
    [navigate]
  );

  // TanStack Table instance
  const table = useReactTable({
    data: genomes,
    columns,
    pageCount: Math.max(1, computedPageCount || 1),
    state: {
      sorting,
      globalFilter,
      columnVisibility,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  // Sort icon component
  const SortIcon = ({ column }) => {
    if (!column.getCanSort()) return null;
    const sorted = column.getIsSorted();
    if (sorted === "asc") return <ChevronUp className="w-4 h-4 ml-1" />;
    if (sorted === "desc") return <ChevronDown className="w-4 h-4 ml-1" />;
    return <ChevronsUpDown className="w-4 h-4 ml-1 opacity-40" />;
  };

  return (
    <>
      <main className="page database-page p-5 bg-white">
        <section className="w-full">
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 text-violet-700 text-xs font-semibold mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
              Genome Database
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-full bg-violet-100">
                <DatabaseIcon className="w-5 h-5 text-violet-700" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
                Origenomi <span className="text-violet-700">Database</span>
              </h1>
            </div>
            <p className="text-sm md:text-base leading-relaxed text-slate-700 max-w-3xl">
              Browse predicted OriC and DnaA regions across bacterial genomes.
            </p>
          </div>

          {/* Search & Controls */}
          <div className="mb-6">
            <div className="bg-white shadow-md rounded-xl p-6 border border-slate-100">
              <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                {/* Search Input */}
                <input
                  type="text"
                  placeholder="Search by organism, RefSeq ID, or NC ID..."
                  className="flex-1 w-full md:w-auto px-4 py-2 border rounded-lg focus:ring-2 focus:ring-violet-500 outline-none"
                  value={globalFilter ?? ""}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                />

                {/* Column Visibility Toggle */}
                <div className="relative">
                  <button
                    onClick={() => setShowColumnMenu(!showColumnMenu)}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition"
                  >
                    <Columns3 className="w-4 h-4" />
                    Columns
                  </button>

                  {showColumnMenu && (
                    <div className="absolute right-0 top-full mt-2 bg-white border border-slate-200 rounded-lg shadow-lg z-50 min-w-[200px]">
                      <div className="p-2">
                        <div className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide border-b border-slate-100 mb-1">
                          Toggle Columns
                        </div>
                        {table.getAllLeafColumns().map((column) => {
                          if (column.id === "actions") return null;
                          return (
                            <label
                              key={column.id}
                              className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 rounded cursor-pointer"
                            >
                              <div
                                className={`w-4 h-4 rounded border flex items-center justify-center transition ${
                                  column.getIsVisible()
                                    ? "bg-violet-600 border-violet-600"
                                    : "border-slate-300"
                                }`}
                              >
                                {column.getIsVisible() && (
                                  <Check className="w-3 h-3 text-white" />
                                )}
                              </div>
                              <input
                                type="checkbox"
                                checked={column.getIsVisible()}
                                onChange={column.getToggleVisibilityHandler()}
                                className="sr-only"
                              />
                              <span className="text-sm text-slate-700">
                                {typeof column.columnDef.header === "string"
                                  ? column.columnDef.header
                                  : column.id}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="mb-20">
            <div className="bg-white shadow-xl rounded-2xl border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-[1500px] w-full">
                  <thead className="bg-slate-100 text-slate-700 text-sm">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className="p-3 font-semibold text-center"
                            style={{ width: header.getSize() }}
                          >
                            {header.isPlaceholder ? null : (
                              <div
                                className={`flex items-center justify-center ${
                                  header.column.getCanSort()
                                    ? "cursor-pointer select-none hover:text-violet-700"
                                    : ""
                                }`}
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                                <SortIcon column={header.column} />
                              </div>
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  <tbody className="divide-y">
                    {/* Loading state */}
                    {loading && (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="py-10 text-center text-gray-500"
                        >
                          <div className="flex flex-col items-center gap-3">
                            <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
                            <span>Loading genomes…</span>
                          </div>
                        </td>
                      </tr>
                    )}

                    {/* No results */}
                    {!loading && table.getRowModel().rows.length === 0 && (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="py-10 text-center text-gray-500"
                        >
                          No genomes found.
                        </td>
                      </tr>
                    )}

                    {/* Table rows */}
                    {!loading &&
                      table.getRowModel().rows.map((row) => (
                        <tr
                          key={row.id}
                          className="hover:bg-slate-50 transition text-center"
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id} className="p-3">
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {!loading && genomes.length > 0 && (
                <div className="flex flex-col md:flex-row items-center justify-between gap-4 px-6 py-4 bg-slate-50 border-t border-slate-100">
                  {/* Page size selector */}
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <span>Show</span>
                    <select
                      value={table.getState().pagination.pageSize}
                      onChange={(e) => {
                        const nextSize = Number(e.target.value);
                        table.setPageSize(nextSize);
                        table.setPageIndex(0);
                      }}
                      className="px-2 py-1 border border-slate-300 rounded-md bg-white focus:ring-2 focus:ring-violet-500 outline-none"
                    >
                      {[10, 25, 50].map((pageSize) => (
                        <option key={pageSize} value={pageSize}>
                          {pageSize}
                        </option>
                      ))}
                    </select>
                    <span>entries</span>
                  </div>

                  {/* Page info */}
                  <div className="text-sm text-slate-600">
                    Showing{" "}
                    <span className="font-medium">
                      {pageStart || 0}
                    </span>
                    {" to "}
                    <span className="font-medium">
                      {pageEnd || 0}
                    </span>
                    {" of "}
                    <span className="font-medium">
                      {total.toLocaleString()}
                    </span>{" "}
                    entries
                  </div>

                  {/* Navigation buttons */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => table.setPageIndex(0)}
                      disabled={!canPreviousPage}
                      className="p-2 rounded-md hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
                      title="First page"
                    >
                      <ChevronsLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => table.previousPage()}
                      disabled={!canPreviousPage}
                      className="p-2 rounded-md hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
                      title="Previous page"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>

                    {/* Page number buttons */}
                    <div className="flex items-center gap-1 mx-2">
                      {(() => {
                        const pageCount = computedPageCount;
                        const currentPage = table.getState().pagination.pageIndex;
                        const pages = [];

                        // Always show first page
                        pages.push(0);

                        // Show ellipsis if needed
                        if (currentPage > 3) {
                          pages.push("...");
                        }

                        // Show pages around current
                        for (
                          let i = Math.max(1, currentPage - 1);
                          i <= Math.min(pageCount - 2, currentPage + 1);
                          i++
                        ) {
                          if (!pages.includes(i)) {
                            pages.push(i);
                          }
                        }

                        // Show ellipsis if needed
                        if (currentPage < pageCount - 4) {
                          pages.push("...");
                        }

                        // Always show last page
                        if (pageCount > 1) {
                          pages.push(pageCount - 1);
                        }

                        return pages.map((page, idx) => {
                          if (page === "...") {
                            return (
                              <span
                                key={`ellipsis-${idx}`}
                                className="px-2 text-slate-400"
                              >
                                …
                              </span>
                            );
                          }
                          return (
                            <button
                              key={page}
                              onClick={() => table.setPageIndex(page)}
                              className={`min-w-[32px] h-8 rounded-md text-sm font-medium transition ${
                                currentPage === page
                                  ? "bg-violet-600 text-white"
                                  : "hover:bg-slate-200 text-slate-700"
                              }`}
                            >
                              {page + 1}
                            </button>
                          );
                        });
                      })()}
                    </div>

                    <button
                      onClick={() => table.nextPage()}
                      disabled={!canNextPage}
                      className="p-2 rounded-md hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
                      title="Next page"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() =>
                        table.setPageIndex(
                          computedPageCount > 0 ? computedPageCount - 1 : 0
                        )
                      }
                      disabled={!canNextPage}
                      className="p-2 rounded-md hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
                      title="Last page"
                    >
                      <ChevronsRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      {/* Close column menu when clicking outside */}
      {showColumnMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowColumnMenu(false)}
        />
      )}

      <Footer />
    </>
  );
};

export default Database;

// requirements : fastapi uvicorn sqlalchemy asyncpg psycopg2-binary

// #CREATE DATABASE genomics;

// # CREATE TABLE refseq (
// #     refseq_id  TEXT PRIMARY KEY,
// #     status     TEXT NOT NULL,
// #     stats      JSONB NOT NULL,
// #     created_at TIMESTAMP DEFAULT NOW()
// # );

// # CREATE TABLE replicon (
// #     id SERIAL PRIMARY KEY,

// #     refseq_id TEXT REFERENCES refseq(refseq_id) ON DELETE CASCADE,

// #     nc_id TEXT NOT NULL,
// #     organism_name TEXT NOT NULL,

// #     dnaA_coords TEXT,
// #     oriC_coords TEXT,

// #     no_rotation_needed BOOLEAN,
// #     rotated_from TEXT,
// #     rotated_shift_bp INT,

// #     original_plots TEXT,
// #     rotated_plots TEXT,
// #     dot_plots TEXT,
// #     pgv_plot TEXT,

// #     created_at TIMESTAMP DEFAULT NOW()
// # );

// # GRANT ALL PRIVILEGES ON TABLE refseq TO anant;
// # GRANT ALL PRIVILEGES ON TABLE replicon TO anant;

// # GRANT USAGE, SELECT ON SEQUENCE replicon_id_seq TO anant;

// #  docker exec -it origenomi-with-database-postgres-1 bash
// # psql -U anant -d origenomi_database

// # docker compose run worker /input_files/GCF_052779095.1_ASM5277909v1_genomic.fna

// # GCF_002812485.1_ASM281248v1_genomic.fna

// # docker run --rm \
// #     -v "$(pwd)/input_files:/input_files" \
// #     -v "$(pwd)/tmp/origenomi:/tmp/origenomi" \
// #     origenomi-with-database-worker:latest \
// #     /input_files/GCF_002812485.1_ASM281248v1_genomic.fna
